// Panel Reclamos ML — lista ml_reclamos con timer deadline + drawer detalle
// Fase 7. Lee de ml_reclamos + join manual con ml_ventas por ml_order_id.

import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import AdminLayout from '../../components/AdminLayout'

const STAGE_META = {
  claim:      { bg: '#FEF3C7', color: '#92400E', label: 'Reclamo' },
  dispute:    { bg: '#FED7AA', color: '#9A3412', label: 'Disputa' },
  mediation:  { bg: '#FEE2E2', color: '#991B1B', label: 'Mediación' },
}
const STATUS_META = {
  opened:      { bg: '#FEF3C7', color: '#92400E', label: 'Abierto' },
  in_process:  { bg: '#DBEAFE', color: '#1E40AF', label: 'En proceso' },
  waiting_response: { bg: '#FEE2E2', color: '#991B1B', label: 'Esperando resp.' },
  closed:      { bg: '#DCFCE7', color: '#166534', label: 'Cerrado' },
}

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('es-UY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—' }
function timerDeadline(dl) {
  if (!dl) return { txt: 'sin plazo', color: '#94A3B8', urgent: false }
  const dif = new Date(dl).getTime() - Date.now()
  const h = Math.floor(dif / (1000 * 60 * 60))
  if (h < 0) return { txt: `Vencido hace ${Math.abs(h)}h`, color: '#DC2626', urgent: true }
  if (h < 24) return { txt: `${h}h restantes`, color: '#DC2626', urgent: true }
  const d = Math.floor(h / 24)
  return { txt: `${d}d restantes`, color: d < 3 ? '#F59E0B' : '#64748B', urgent: d < 3 }
}

export default function ReclamosML() {
  const [reclamos, setReclamos] = useState([])
  const [ventas, setVentas] = useState({}) // ml_order_id → venta
  const [loading, setLoading] = useState(true)
  const [filtroStatus, setFiltroStatus] = useState('abiertos')
  const [drawer, setDrawer] = useState(null)

  useEffect(() => { loadData() }, [])
  async function loadData() {
    setLoading(true)
    const { data: recs } = await supabase.from('ml_reclamos')
      .select('id, ml_claim_id, ml_order_id, stage, status, type, reason_name, deadlines, resolution, date_created, last_updated, raw_payload, notas_internas, resuelto_at')
      .order('date_created', { ascending: false }).limit(500)
    const orderIds = (recs || []).map(r => r.ml_order_id).filter(Boolean)
    const ventasMap = {}
    if (orderIds.length) {
      const { data: vs } = await supabase.from('ml_ventas').select('ml_order_id, cliente_nickname, monto_bruto, producto_id').in('ml_order_id', orderIds)
      for (const v of vs || []) ventasMap[v.ml_order_id] = v
    }
    setReclamos(recs || [])
    setVentas(ventasMap)
    setLoading(false)
  }

  const kpis = useMemo(() => {
    const abiertos = reclamos.filter(r => r.status !== 'closed')
    const urgentes = abiertos.filter(r => {
      const dl = r.deadlines?.expiration
      if (!dl) return false
      const h = (new Date(dl).getTime() - Date.now()) / 3600000
      return h < 24
    })
    const vencidos = abiertos.filter(r => {
      const dl = r.deadlines?.expiration
      return dl && new Date(dl).getTime() < Date.now()
    })
    return { total: reclamos.length, abiertos: abiertos.length, urgentes: urgentes.length, vencidos: vencidos.length }
  }, [reclamos])

  const filtrados = useMemo(() => {
    if (filtroStatus === 'todos') return reclamos
    if (filtroStatus === 'abiertos') return reclamos.filter(r => r.status !== 'closed')
    if (filtroStatus === 'urgentes') return reclamos.filter(r => {
      if (r.status === 'closed') return false
      const dl = r.deadlines?.expiration
      if (!dl) return false
      return (new Date(dl).getTime() - Date.now()) / 3600000 < 24
    })
    if (filtroStatus === 'cerrados') return reclamos.filter(r => r.status === 'closed')
    return reclamos
  }, [reclamos, filtroStatus])

  return (
    <AdminLayout>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <h1 style={{ fontSize: 22, color: '#1A3461', margin: 0 }}>Reclamos MercadoLibre</h1>
            <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>Reclamos · disputas · mediaciones. Timer deadline crítico: si vence, ML resuelve a favor del comprador.</div>
          </div>
          <button onClick={loadData} style={{ padding: '7px 14px', background: '#1A3461', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>↻ Actualizar</button>
        </div>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
          <KpiCard label="Total reclamos" valor={kpis.total} />
          <KpiCard label="Abiertos" valor={kpis.abiertos} color={kpis.abiertos > 0 ? '#F59E0B' : '#94A3B8'} />
          <KpiCard label="Urgentes <24h" valor={kpis.urgentes} color={kpis.urgentes > 0 ? '#DC2626' : '#10B981'} sub={kpis.urgentes > 0 ? '⚠ atender ya' : 'todo OK'} />
          <KpiCard label="Vencidos" valor={kpis.vencidos} color={kpis.vencidos > 0 ? '#DC2626' : '#10B981'} sub={kpis.vencidos > 0 ? '❌ perdidos' : 'ninguno'} />
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {[
            { v: 'abiertos', l: 'Abiertos' },
            { v: 'urgentes', l: '⚠ Urgentes <24h' },
            { v: 'cerrados', l: 'Cerrados' },
            { v: 'todos', l: 'Todos' },
          ].map(f => (
            <button key={f.v} onClick={() => setFiltroStatus(f.v)} style={{
              padding: '5px 11px', fontSize: 11, fontWeight: 700, borderRadius: 999, border: 'none',
              background: filtroStatus === f.v ? '#1A3461' : '#F1F5F9',
              color: filtroStatus === f.v ? '#fff' : '#475569', cursor: 'pointer',
            }}>{f.l}</button>
          ))}
        </div>

        {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Cargando…</div> :
         filtrados.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8', background: '#F8FAFC', borderRadius: 8 }}>
            {reclamos.length === 0 ? 'Todavía no hay reclamos ML. Cuando llegue el primer webhook claims aparecerá acá.' : 'Ningún reclamo con este filtro.'}
          </div>
         ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
            <thead style={{ background: '#F8FAFC', color: '#475569', textAlign: 'left' }}>
              <tr>
                <th style={{ padding: '10px 8px' }}>Creado</th>
                <th>Claim · Order</th>
                <th>Comprador</th>
                <th>Etapa</th>
                <th>Status</th>
                <th>Motivo</th>
                <th>Deadline</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map(r => {
                const stageM = STAGE_META[r.stage] || { bg: '#F1F5F9', color: '#475569', label: r.stage || '—' }
                const statusM = STATUS_META[r.status] || { bg: '#F1F5F9', color: '#475569', label: r.status || '—' }
                const timer = timerDeadline(r.deadlines?.expiration)
                const venta = ventas[r.ml_order_id]
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid #E2E8F0', cursor: 'pointer', background: timer.urgent && r.status !== 'closed' ? '#FEF2F2' : 'transparent' }} onClick={() => setDrawer(r)}>
                    <td style={{ padding: '8px 8px', color: '#64748B' }}>{fmtDate(r.date_created)}</td>
                    <td>
                      <code style={{ fontSize: 10.5, background: '#F8FAFC', padding: '1px 5px', borderRadius: 3 }}>{r.ml_claim_id}</code>
                      <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>order {r.ml_order_id || '—'}</div>
                    </td>
                    <td style={{ fontWeight: 700, color: '#1A3461' }}>{venta?.cliente_nickname || '—'}</td>
                    <td><span style={{ fontSize: 10.5, padding: '2px 8px', borderRadius: 999, background: stageM.bg, color: stageM.color, fontWeight: 800 }}>{stageM.label}</span></td>
                    <td><span style={{ fontSize: 10.5, padding: '2px 8px', borderRadius: 999, background: statusM.bg, color: statusM.color, fontWeight: 800 }}>{statusM.label}</span></td>
                    <td style={{ fontSize: 11, color: '#475569' }}>{r.reason_name || r.type || '—'}</td>
                    <td style={{ fontSize: 11, color: timer.color, fontWeight: timer.urgent ? 700 : 500 }}>{timer.txt}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
         )}

        {/* Drawer detalle */}
        {drawer && (
          <div onClick={() => setDrawer(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={e => e.stopPropagation()} style={{ width: 520, maxWidth: '100%', background: '#fff', padding: 22, overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div>
                  <h3 style={{ fontSize: 18, color: '#1A3461', margin: 0 }}>Reclamo #{drawer.ml_claim_id}</h3>
                  <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>Order {drawer.ml_order_id}</div>
                </div>
                <button onClick={() => setDrawer(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94A3B8' }}>×</button>
              </div>
              <div style={{ fontSize: 12, color: '#475569', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', marginBottom: 16 }}>
                <span>Etapa</span><strong>{drawer.stage || '—'}</strong>
                <span>Status</span><strong>{drawer.status || '—'}</strong>
                <span>Tipo</span><strong>{drawer.type || '—'}</strong>
                <span>Motivo</span><strong>{drawer.reason_name || '—'}</strong>
                <span>Creado</span><strong>{fmtDate(drawer.date_created)}</strong>
                <span>Última actualización</span><strong>{fmtDate(drawer.last_updated)}</strong>
                <span>Deadline expiración</span><strong style={{ color: timerDeadline(drawer.deadlines?.expiration).color }}>{timerDeadline(drawer.deadlines?.expiration).txt}</strong>
                {drawer.deadlines?.resolution && <><span>Deadline resolución</span><strong>{fmtDate(drawer.deadlines.resolution)}</strong></>}
                {drawer.resuelto_at && <><span>Resuelto</span><strong style={{ color: '#10B981' }}>{fmtDate(drawer.resuelto_at)}</strong></>}
              </div>
              <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', padding: 10, borderRadius: 6, fontSize: 11, color: '#78350F', marginBottom: 14 }}>
                💡 Los reclamos se responden desde el chat de MercadoLibre. Este panel es solo informativo + tracking del deadline. Abrí el chat directamente en <a href={`https://www.mercadolibre.com.uy/gz/home?claim_id=${drawer.ml_claim_id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#0369A1' }}>ML</a>.
              </div>
              <details>
                <summary style={{ cursor: 'pointer', fontSize: 11, color: '#94A3B8' }}>Ver JSON crudo de ML</summary>
                <pre style={{ background: '#F8FAFC', padding: 10, borderRadius: 6, fontSize: 10, overflow: 'auto', maxHeight: 300, marginTop: 6 }}>{JSON.stringify(drawer.raw_payload, null, 2)}</pre>
              </details>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

function KpiCard({ label, valor, sub, color = '#1A3461' }) {
  return (
    <div style={{ background: '#fff', padding: 12, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
      <div style={{ fontSize: 10.5, color: '#94A3B8', letterSpacing: '.05em', textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 22, color, fontWeight: 800, marginTop: 4 }}>{valor}</div>
      {sub && <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}
